import { https } from "../http";
import store from "../store";
import {
  setLoading,
  setReports,
  setKpi,
  setClientsForProvider,
  setClientsLoading,
  setPastDue,
  setPastDueLoading,
  setFailed,
  setFailedLoading,
  setSettings,
  setSettingsLoading,
  setSigners,
  setSignersLoading,
  setDraft,
  setDraftLoading,
  setDraftSaving,
  setGenerationBatch,
} from "../store/reducers/quarterlyReport";
import { showError } from "../utils/index";
import { uploadFileToS3Api } from "../utils/PresignedUrlFunction";

// Evidence lands under its own S3 folder rather than the shared
// profile_photos/ default; the backend picks the key (OD-1480).
const REPORT_EVIDENCE_PREFIX = "report_evidence";
export const ALLOWED_EVIDENCE_EXTENSIONS = ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg", "png", "heic", "heif"];

// All endpoints live under /report/quarterly-pcr/* — a namespace distinct
// from the existing /report/* (DARS-style) and /report/provider-quarterly-*
// (facility operational report) endpoints, since this is a different
// document type (per-client DBHDS Person-Centered Review).
const BASE = "/report/quarterly-pcr";

// The list is refetched on every search/filter/page change, so several
// requests can be in flight at once and they do not necessarily come back in
// the order they were sent. Only the newest one is allowed to write to the
// store — otherwise a slow earlier response landing last repaints the table
// with rows for a query the user has already moved on from (and clears the
// loading flag while the current request is still running).
let listRequestSeq = 0;

// GET the dashboard list + KPI counts (This Month / Past Due / Failed).
const getQuarterlyReports = async (page = 1, status = "all", search = "", showLoading = true) => {
  const requestId = ++listRequestSeq;
  try {
    if (showLoading) store.dispatch(setLoading(true));
    const response = await https().get(
      `${BASE}?page=${page}&status=${status}&search=${encodeURIComponent(search)}`
    );
    if (requestId !== listRequestSeq) return;
    if (response?.status === 200) {
      store.dispatch(setReports(response?.data));
      store.dispatch(setKpi(response?.data?.kpi));
      return response?.data;
    }
  } catch (error) {
    // A superseded request's failure is not the user's problem — the request
    // that replaced it owns both the error toast and the loading flag.
    if (requestId !== listRequestSeq) return;
    store.dispatch(setLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// GET clients for a provider/facility scoped to a review period, with note
// counts and No-active-ISP / No-approved-notes flags (Story 2).
const getClientsForGenerate = async (providerId, facilityId, startDate, endDate) => {
  try {
    store.dispatch(setClientsLoading(true));
    const response = await https().post(`${BASE}/clients`, {
      providerId,
      facilityId,
      startDate,
      endDate,
    });
    if (response?.status === 200) {
      store.dispatch(setClientsForProvider(response?.data?.clients || []));
      return response?.data?.clients;
    }
  } catch (error) {
    store.dispatch(setClientsLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
    return [];
  }
};

// GET the Review Period quarter presets — calendar-year quarters, or (per
// the "Quarter basis" setting) quarters anchored to the selected provider's
// clients' own ISP plan year. Returns { basis, anchored, presets }.
const getQuarterPresets = async (providerId) => {
  try {
    const query = providerId ? `?providerId=${providerId}` : "";
    const response = await https().get(`${BASE}/quarter-presets${query}`);
    if (response?.status === 200) return response?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
  return null;
};

// POST to start batch generation; returns a batchId to poll for progress.
const startGeneration = async (providerId, facilityId, clientIds, startDate, endDate) => {
  try {
    const response = await https().post(`${BASE}/generate`, {
      providerId,
      facilityId,
      clientIds,
      startDate,
      endDate,
    });
    if (response?.status === 200) {
      store.dispatch(setGenerationBatch(response?.data));
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return null;
  }
};

// GET per-client progress for a batch — poll this while generation is running.
const getGenerationProgress = async (batchId) => {
  try {
    const response = await https().get(`${BASE}/generate/${batchId}`);
    if (response?.status === 200) {
      store.dispatch(setGenerationBatch(response?.data));
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return null;
  }
};

// POST to retry one failed client within a batch (or from the Failed Reports page).
const retryGenerationRow = async (batchId, clientId) => {
  try {
    const response = await https().post(`${BASE}/generate/${batchId}/retry`, { clientId });
    if (response?.status === 200) {
      store.dispatch(setGenerationBatch(response?.data));
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return null;
  }
};

// GET the full drafted/finalized report (Section A/B/C + Completion) for review.
const getQuarterlyReportDraft = async (reportId, showLoading = true) => {
  try {
    if (showLoading) store.dispatch(setDraftLoading(true));
    const response = await https().get(`${BASE}/${reportId}`);
    if (response?.status === 200) {
      store.dispatch(setDraft(response?.data));
      return response?.data;
    }
  } catch (error) {
    store.dispatch(setDraftLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// GET a report's full content without touching the review-screen's draft
// state — used for one-off actions like the list's row-level PDF download,
// which shouldn't disturb whatever report is currently open for editing.
const getReportForExport = async (reportId) => {
  try {
    const response = await https().get(`${BASE}/${reportId}`);
    if (response?.status === 200) return response?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
  return null;
};

// PUT edits to a report while it's Awaiting Review.
const saveQuarterlyReportDraft = async (reportId, data, callback) => {
  try {
    store.dispatch(setDraftSaving(true));
    const response = await https().put(`${BASE}/${reportId}`, data);
    if (response?.status === 200) {
      store.dispatch(setDraft(response?.data));
      callback?.();
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  } finally {
    store.dispatch(setDraftSaving(false));
  }
};

// POST the signature — completes and locks the report per Story 5.
//
// A report whose selections the engine could not evidence answers 409 unless
// the body carries acknowledgeFlags. That is the normal path for a flagged
// report, not a failure, so the 409 is handed to onNeedsAcknowledge to be
// confirmed rather than surfaced as an error toast.
const signQuarterlyReport = async (reportId, data, callback, onNeedsAcknowledge) => {
  try {
    store.dispatch(setDraftSaving(true));
    const response = await https().post(`${BASE}/${reportId}/sign`, data);
    if (response?.status === 200) {
      store.dispatch(setDraft(response?.data));
      callback?.(response?.data);
    }
  } catch (error) {
    if (error?.response?.status === 409 && onNeedsAcknowledge) {
      onNeedsAcknowledge(error?.response?.data?.message);
      return;
    }
    showError(error?.response?.data?.message, error?.response?.status);
  } finally {
    store.dispatch(setDraftSaving(false));
  }
};

// DELETE a report from the list (Awaiting Review or Final).
const deleteQuarterlyReport = async (reportId, callback) => {
  try {
    const response = await https().delete(`${BASE}/${reportId}`);
    if (response?.status === 200) {
      callback?.();
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// POST a blank manual (no-AI) report for an external provider. Nothing is
// drafted — every section comes back empty for the reviewer to hand-enter.
const createManualReport = async (data, callback) => {
  try {
    store.dispatch(setDraftSaving(true));
    const response = await https().post(`${BASE}/manual`, data);
    if (response?.status === 200) {
      store.dispatch(setDraft(response?.data));
      callback?.(response?.data);
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  } finally {
    store.dispatch(setDraftSaving(false));
  }
  return null;
};

// Uploads one evidence file straight to S3 via the existing presigned pipeline
// (a manual report can be created after its first piece of evidence, see
// registerReportEvidence, so the upload doesn't depend on one). Returns bare file metadata; nothing
// is recorded on any report yet. `allowedTypes` should be the contractor's
// live `manualEvidenceTypes` setting (falls back to the full default list) so
// this pre-flight check matches exactly what the server will accept.
const uploadEvidenceFile = async (file, allowedTypes, reportId) => {
  const allowed = allowedTypes?.length ? allowedTypes : ALLOWED_EVIDENCE_EXTENSIONS;
  const extension = file?.name?.split(".").pop()?.toLowerCase();
  if (!allowed.includes(extension)) {
    showError(`Unsupported file type ".${extension || ""}". Allowed: ${allowed.join(", ").toUpperCase()}.`);
    return null;
  }
  try {
    store.dispatch(setDraftSaving(true));
    const { uploadURL } = await uploadFileToS3Api({
      file,
      category: REPORT_EVIDENCE_PREFIX,
    });
    return { name: file.name, fileUrl: uploadURL, fileType: file.type, fileSize: file.size };
  } catch (error) {
    showError(error?.message, undefined);
    return null;
  } finally {
    store.dispatch(setDraftSaving(false));
  }
};

// Records an already-uploaded evidence file (see uploadEvidenceFile) against
// a real report. Reference material only — never read by AI.
const registerReportEvidence = async (reportId, fileMeta) => {
  try {
    store.dispatch(setDraftSaving(true));
    const response = await https().post(`${BASE}/${reportId}/evidence`, fileMeta);
    if (response?.status === 200) return response?.data;
  } catch (error) {
    showError(error?.response?.data?.message || error?.message, error?.response?.status);
  } finally {
    store.dispatch(setDraftSaving(false));
  }
  return null;
};

// Combines the two for a report that already exists — upload then register
// in one call, same as before this was split to support attaching evidence
// ahead of a manual report's first Save.
const uploadReportEvidence = async (reportId, file, allowedTypes) => {
  const fileMeta = await uploadEvidenceFile(file, allowedTypes, reportId);
  if (!fileMeta) return null;
  return registerReportEvidence(reportId, fileMeta);
};

const deleteReportEvidence = async (reportId, evidenceId) => {
  try {
    store.dispatch(setDraftSaving(true));
    const response = await https().delete(`${BASE}/${reportId}/evidence/${evidenceId}`);
    if (response?.status === 200) return response?.data;
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
  } finally {
    store.dispatch(setDraftSaving(false));
  }
  return null;
};

// GET the Past Due Quarterly Report worklist (Story 6).
const getPastDueReports = async (showLoading = true) => {
  try {
    if (showLoading) store.dispatch(setPastDueLoading(true));
    const response = await https().get(`${BASE}/past-due`);
    if (response?.status === 200) {
      store.dispatch(setPastDue(response?.data?.rows || []));
      return response?.data?.rows;
    }
  } catch (error) {
    store.dispatch(setPastDueLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// GET the Failed Reports list (Story 7).
const getFailedReports = async (showLoading = true) => {
  try {
    if (showLoading) store.dispatch(setFailedLoading(true));
    const response = await https().get(`${BASE}/failed`);
    if (response?.status === 200) {
      store.dispatch(setFailed(response?.data?.rows || []));
      return response?.data?.rows;
    }
  } catch (error) {
    store.dispatch(setFailedLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// POST retry directly from the Failed Reports page.
const retryFailedReport = async (reportId) => {
  try {
    const response = await https().post(`${BASE}/failed/${reportId}/retry`);
    if (response?.status === 200) {
      // Awaited: the caller keeps its row spinner up until this resolves, and
      // clearing it before the refreshed list arrives is exactly what made the
      // retried row sit there with its old status.
      await getFailedReports(false);
      return response?.data;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    // The backend now re-checks duplicate/no-notes-skip/no-ISP-skip/client-
    // existence before drafting and updates this row's errorMessage/failedOn
    // when blocked (not just on a real drafting exception) — refresh so the
    // list shows the current reason instead of the stale pre-retry one.
    await getFailedReports(false);
    return null;
  }
};

// GET/PUT report settings (Tara drafting rules, who can sign, lock-on-finalize).
const getReportSettings = async () => {
  try {
    store.dispatch(setSettingsLoading(true));
    const response = await https().get(`${BASE}/settings`);
    if (response?.status === 200) {
      store.dispatch(setSettings(response?.data));
      return response?.data;
    }
  } catch (error) {
    store.dispatch(setSettingsLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

const updateReportSettings = async (data) => {
  try {
    store.dispatch(setSettingsLoading(true));
    const response = await https().put(`${BASE}/settings`, data);
    if (response?.status === 200) {
      store.dispatch(setSettings(response?.data));
      return response?.data;
    }
  } catch (error) {
    store.dispatch(setSettingsLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// GET staff eligible to sign, per the "Who can sign" setting.
const getEligibleSigners = async () => {
  try {
    store.dispatch(setSignersLoading(true));
    const response = await https().get(`${BASE}/signers`);
    if (response?.status === 200) {
      store.dispatch(setSigners(response?.data?.signers || []));
      return response?.data?.signers;
    }
  } catch (error) {
    store.dispatch(setSignersLoading(false));
    showError(error?.response?.data?.message, error?.response?.status);
  }
};

// POST a question to the Tara Ai assistant, scoped to the contractor's
// approved providers only (Story 3). `history` is the prior chat turns
// ({kind:"user"|"bot", text}) so the assistant has real multi-turn context
// (e.g. a follow-up question about the client just discussed). Returns reply.
const askTaraAi = async (question, context, history) => {
  try {
    const response = await https().post(`${BASE}/tara-ai/ask`, { question, context, history });
    if (response?.status === 200) {
      return response?.data?.reply;
    }
  } catch (error) {
    showError(error?.response?.data?.message, error?.response?.status);
    return null;
  }
};

export {
  getQuarterlyReports,
  getClientsForGenerate,
  getQuarterPresets,
  startGeneration,
  getGenerationProgress,
  retryGenerationRow,
  getQuarterlyReportDraft,
  getReportForExport,
  saveQuarterlyReportDraft,
  signQuarterlyReport,
  deleteQuarterlyReport,
  createManualReport,
  uploadReportEvidence,
  uploadEvidenceFile,
  registerReportEvidence,
  deleteReportEvidence,
  getPastDueReports,
  getFailedReports,
  retryFailedReport,
  getReportSettings,
  updateReportSettings,
  getEligibleSigners,
  askTaraAi,
};
